<template>
  <div v-if="hasAccess(MARKETING_MATERIALS_ACCESS, MARKETING_MATERIALS_ACCESS_MANAGE)"
       ref="flyer"
       class="marketing-materials"
  >
    <h1>Flyer</h1>
    <template v-if="pages && pages.length === 0">
      <notification-financing-page/>
    </template>
    <template v-else-if="pages">
      <PageSelector />

      <div class="flyer-about columns">
        <div class="column">
          <div class="flyer-about-use flyer-about-item">
            <b-field>
              <p>{{ description }}</p>
            </b-field>
            <div class="flyer-examples"
                 v-if="flyerFrontSide || flyerBackSide">
              <img class="" :src="flyerFrontSide"
                   alt="Flyer 1 example">
              <img class="" :src="flyerBackSide"
                   alt="Flyer 2 example">
            </div>
            <div class="flyer-about-use-btn-box">
              <b-button
                  type="is-success"
                  @click="onDownloadFlyerClick"
                  class="download-flyer">DOWNLOAD FLYER
              </b-button>
            </div>
          </div>
        </div>
        <div class="column">
          <div class="flyer-about-manage flyer-about-item">
            <p>
              Manage your flyer with ease. Here you can change/edit your company name, contact
              information, and logo that will appear in the flyer.
            </p>
            <p class="flyer-about-manage-title">
              Show your company contact information on your flyer
            </p>
            <validation-observer v-slot="{ handleSubmit, reset }" ref="obs">
              <form class="flyer-form"
                  @reset.prevent="resetForm(reset)"
                  @submit.prevent="handleSubmit(submit)">
                <field
                    name="Company name"
                    v-model="fieldsData.companyName"
                    :editable="editable"
                    rules="max:100" maxlength="100"
                    class="word-break"
                    :blur="() => { trimSpaces('companyName') }"
                >
                </field>
                <template>
                  <template v-if="isEdge()">
                    <template v-if="editable">
                      <input type="file" ref="logo">
                    </template>
                    <template v-else>
                      <div class="image-section-image-wrapper">
                        <img class="image"
                             :alt="fieldsData.companyName"
                             :src="fieldsData.logo | logo">
                      </div>
                    </template>
                  </template>
                  <template v-else>
                    <validation-provider v-slot="{ errors }"  rules="image|size:2048">
                      <b-field name="Company Logo For Co-Branded Page"
                               class="personal-info__field"
                               :type="{ 'is-danger': errors.length }"
                               :message="errors"
                               custom-class="personal-info__field-label">
                        <template slot="label">
                          Logo image<b-tooltip v-if="editable"
                            label="Use your company logo as a brand element. To upload your logo,
                             click “CHANGE LOGO” and upload a file, if necessary, crop the uploaded
                             image, and click “DONE”. If you do not have your own logo, a default
                             image will be used on your financing page."
                                     type="is-info"
                                     class="is-gray"
                                     size="is-small"
                                     position="is-right"
                                     :delay="200"
                                     animated
                                     multilined>
                            <b-icon pack="fa" size="is-small" icon="question-circle">
                            </b-icon>
                          </b-tooltip>
                        </template>
                      </b-field>
                    </validation-provider>
                  </template>
                  <template v-if="editable">
                    <p class="p-logo">
                      Upload your logo in PNG, GIF, or JPG format. Recommended size: 218×144
                      px. Max. file size: 1 MB.
                    </p>
                    <b-field class="image-section">
                      <div class="image-section-image-wrapper">
                        <img ref="image"
                             :alt="fieldsData.companyName"
                             :src="(fieldsData.logo && fieldsData.logo !== NO_LOGO_IMAGE
                             && fieldsData.logo !== NO_LOGO_IMAGE_URL)
                             ? fieldsData.logo : NO_LOGO_IMAGE_URL"
                        >
                      </div>
                      <div class="image-section-btns">
                        <b-button type="outlined"
                                  onclick="document.getElementById('file-input').click();">
                          CHANGE LOGO
                        </b-button>
                        <b-button :disabled="isDisabledDeleteLogo"
                                  type="outlined"
                                  @click="delImage">
                          DELETE LOGO
                        </b-button>
                      </div>
                    </b-field>
                    <input
                        accept="image/jpeg, image/png, image/gif"
                        @change="replace"
                        ref="fileInput"
                        id="file-input"
                        type="file"
                        name="name"
                        style="display: none" />
                  </template>
                  <template v-else>
                    <p class="p-logo">
                      Upload your logo in PNG, GIF, or JPG format. Recommended size: 218×144
                      px. Max. file size: 1 MB.
                    </p>
                    <!--suppress HtmlUnknownTarget -->
                    <div class="image-section-image-wrapper">

                      <img class="image"
                           :alt="fieldsData.companyName"
                           :src="(fieldsData.logo && fieldsData.logo !== NO_LOGO_IMAGE
                           && fieldsData.logo !== NO_LOGO_IMAGE_URL) ?
                           fieldsData.logo : NO_LOGO_IMAGE_URL">
                    </div>
                  </template>
                </template>
                <div>
                  <div class="fields-contact-number-extension">
                    <!-- todo: use ModernPhoneInput component -->
                    <field
                        class="contact-number"
                        v-model.trim="fieldsData.phone"
                        name="Contact number"
                        rules="phone" maxlength="10"
                        :editable="editable"
                        :blur="onPhoneBlur">
                      <template slot="raw">
                        {{ fieldsData.phone | phone }}
                      </template>
                    </field>
                    <field
                        v-model.trim="fieldsData.extPhone"
                        name="Extension"
                        :tooltip="'An extension is a short internal number assigned to an' +
                         ' employee, a project team, org department of your main contact number.'"
                        :position=getTooltipPosition
                        rules="ext_phone" maxlength="6"
                        :editable="editable"
                        :disabled="extPhoneDisabled"
                        class="extPhoneField"
                        icon-pack="fa"
                        tooltip-classes="is-gray"
                        :size="'is-small'"
                        :blur="onExtPhoneBlur">
                      <template slot="raw">
                        {{ fieldsData.extPhone }}
                      </template>
                    </field>
                  </div>
                  <field
                      :name="`Company Website URL${(isRequiredWebsiteUrl)? '*' : ''}`"
                      v-model="fieldsData.websiteUrl"
                      :editable="editable"
                      :message="editable ? ['&nbsp;'] : []"
                      :rules="`${(isRequiredWebsiteUrl)
                      ? 'required|' : ''}url_without_protocol|max:128`"
                      maxlength="128"
                      :blur="() => { trimSpaces('websiteUrl') }"
                  >
                    <template v-slot:raw>
                      <a :href="fieldsData.websiteUrl" target="_blank">
                        {{ fieldsData.websiteUrl }}
                      </a>
                    </template>
                  </field>
                </div>
                <template>
                  <b-field label="QR code on backside" custom-class="personal-info__field-label"
                           class="qr-code-backside-field"
                           :class="{ 'disabledSelect': !editable }">
                    <b-select v-model="fieldsData.qrCodeBackside" :disabled="!editable"
                    class="qr-code-selector">
                      <option
                          v-for="option in qrCodeBacksideOptions"
                          :key="option"
                          :value="option"
                          :selected="option === flyer.qrCodeBackside"
                      >{{ option }}
                      </option>
                    </b-select>
                  </b-field>
                </template>
                <div class="flyer-about-manage-btn-box">
                  <template v-if="editable">
                    <button class="button outlined" type="reset">cancel</button>
                    <button id="flyer-submit-btn"
                            class="button is-success" type="submit" :disabled="submitDisabled">
                      submit
                    </button>
                  </template>
                  <template v-else>
                    <b-button
                        type="is-success"
                        @click="setEditable"
                        class="edit-flyer is-outlined"
                    > EDIT FLYER </b-button>
                  </template>
                </div>
              </form>
            </validation-observer>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import {
  mapActions,
  mapGetters,
  mapMutations,
  mapState,
} from 'vuex';
import * as CONTRACTOR_ACTIONS from '@/store/modules/contractor/actions';
import {
  updateContractorPageMarketingMaterials,
} from '@/api/contractor';
import * as MUTATIONS from '@/store/mutations';
import _ from 'lodash';
import { getFlyerContractor, getFlyerContractorImages } from '@/api/flyer_templates';
import Vue from 'vue';
import EditImage from '@/components/Modal/EditImage.vue';
import NotificationFinancingPage from '@/components/NotificationFinancingPage.vue';
import Field from '@/components/Field.vue';
import PageSelector from '@/views/MarketingMaterials/components/PageSelector.vue';
import {
  CUSTOMER_TYPE_GENERAL_ID,
  CUSTOMER_TYPE_MEDICAL_ID,
} from '@/const/constants';

export default {
  name: 'Flyer',
  components: { PageSelector, Field, NotificationFinancingPage },
  data() {
    return {
      flyerFrontSide: null,
      flyerBackSide: null,
      flyer: { qrCodeBackside: 'Co-branded page' },
      fieldsData: {
        companyName: '',
        websiteUrl: '',
        phone: null,
        extPhone: null,
        logo: null,
        qrCodeBackside: null,
        logoFileName: null,
      },
      editable: false,
      submitDisabled: false,
      qrCodeBacksideOptions: ['Co-branded page', 'Company website'],
      cropModal: null,
      MARKETING_MATERIALS_ACCESS: { resource: 'marketing-materials', code: 'access' },
      MARKETING_MATERIALS_ACCESS_MANAGE: { resource: 'marketing-materials', code: 'manage_access' },
      NO_LOGO_IMAGE_URL: '',
      NO_LOGO_IMAGE: '',
      isActivated: false,
    };
  },
  deactivated() {
    this.isActivated = false;
  },
  async activated() {
    if (!this.contractorId) return;
    this.startLoading();

    try {
      if (this.contractor.typeId) {
        this.NO_LOGO_IMAGE = `no-logo_type_${this.contractor.typeId}.png`;
        this.NO_LOGO_IMAGE_URL = `${process.env.VUE_APP_CDN_GET_URL}no-logo_type_${this.contractor.typeId}.png`;
      }
      if (!this.selectedPage?.id) {
        await this.updPages(this.contractorId);
      }

      if (this.selectedPage?.id) {
        await this.changePage();
      }
    } catch (error) {
      this.$buefy.snackbar.open({
        message: 'An unexpected error occurred, please contact support@enhancify.com',
        actionText: ' ',
        position: 'is-top',
        indefinite: true,
        type: 'is-danger',
      });
    }

    this.isActivated = true;
    this.finishLoading();
    window.scrollTo(0, 0);
  },
  computed: {
    ...mapState('contractor', {
      contractorId: (state) => state.id,
      contractorWebsiteUrl: (state) => state.websiteUrl,
      contractorTypeId: (state) => state.typeId,
    }),
    ...mapState('user', { userId: (state) => state.id }),
    ...mapGetters(['hasAccess', 'sidebarWidth']),
    ...mapGetters('contractor', ['selectedPage', 'pages']),
    ...mapState(['contractor']),
    description() {
      let introduceTo = 'customers';
      let sendTo = 'customers';
      let showTo = 'customers';

      if (this.contractorTypeId === CUSTOMER_TYPE_MEDICAL_ID) {
        introduceTo = 'patients';
        sendTo = 'clients';
        showTo = 'patients';
      } else if (this.contractorTypeId === CUSTOMER_TYPE_GENERAL_ID) {
        showTo = 'clients';
      }

      return `A two-page flyer will help you introduce your ${introduceTo} to financing and its`
        + ` benefits. You can print the flyer out, send it out to your ${sendTo}, or add it to`
        + ` your site to show your ${showTo} you offer financing.`;
    },
    isRequiredWebsiteUrl() {
      return this.fieldsData.qrCodeBackside === 'Company website';
    },
    extPhoneDisabled() {
      return !this.fieldsData.phone;
    },
    isDisabledDeleteLogo() {
      return !this.fieldsData.logo || this.fieldsData.logo === this.NO_LOGO_IMAGE_URL
        || this.fieldsData.logo === this.NO_LOGO_IMAGE;
    },
    windowWidth() {
      return this.$store.state.windowWidth;
    },
    getTooltipPosition() {
      const widthWithoutSidebar = this.windowWidth - this.sidebarWidth;
      if (widthWithoutSidebar > 353 && widthWithoutSidebar < 500) {
        return 'is-left';
      }
      if (widthWithoutSidebar >= 1088 && widthWithoutSidebar <= 1152 && this.editable) {
        return 'is-top';
      }
      return 'is-right';
    },
  },
  watch: {
    async selectedPage() {
      if (this.isActivated) {
        await this.changePage();
      }
    },
  },
  filters: {
    emptyLogo(value) {
      return value || this.NO_LOGO_IMAGE_URL;
    },
  },
  methods: {
    ...mapActions('contractor', { updPages: CONTRACTOR_ACTIONS.LOAD_PAGES }),
    ...mapMutations({
      startLoading: MUTATIONS.START_LOADING,
      finishLoading: MUTATIONS.FINISH_LOADING,
    }),
    ...mapActions('contractor', {
      setSelectedPage: CONTRACTOR_ACTIONS.SET_SELECTED_PAGE,
      getContractor: CONTRACTOR_ACTIONS.CONTRACTOR,
    }),
    ...mapMutations({ setSidebarWidth: MUTATIONS.SET_SIDEBAR_WIDTH }),

    onDownloadFlyerClick() {
      if (this.contractorId === null) return;
      this.startLoading();
      getFlyerContractor(
        this.contractorId,
        this.selectedPage.id,
      ).then((response) => {
        if (response.headers['content-type'] === 'application/json') {
          const fReader = new FileReader();
          fReader.readAsText(response.data);
          fReader.addEventListener('loadend', () => {
            const data = JSON.parse(fReader.result);
            if (data.error) {
              this.$buefy.toast.open({ message: 'An unexpected error occurred, please contact support@enhancify.com', type: 'is-danger' });
            }
          });
        } else {
          const file = window.URL.createObjectURL(new Blob([response.data], {
            type: 'application/pdf',
          }));
          window.open(file);
        }
      })
        .finally(() => {
          this.finishLoading();
        });
    },
    async changePage() {
      this.initFlyerParams();
      this.fieldsData = _.clone(this.flyer);
      await this.getFlyerImages();
      this.setNotEditable();
    },
    trimSpaces(field) {
      this.fieldsData[field] = this.fieldsData[field].trim().replace(/\s+/gu, ' ');
      if (field === 'websiteUrl') {
        this.fieldsData[field] = this.fieldsData[field].replace(/([\s]+)/gu, '');
        this.fieldsData[field] = this.fieldsData[field].length > 0
        && this.fieldsData[field].slice(0, 4) !== 'http'
          ? `http://${this.fieldsData[field]}` : this.fieldsData[field];
      }
    },
    isEdge() {
      return /Edge/.test(navigator.userAgent);
    },
    setEditable() {
      this.editable = true;
    },
    setNotEditable() {
      this.editable = false;
    },
    resetForm(reset = null) {
      this.setNotEditable();
      this.resetFieldsData();
      if (typeof reset === 'function') reset();
      if (this.$refs.obs.flags.invalid) this.$refs.obs.reset();
    },
    resetFieldsData() {
      this.initFlyerParams();
      this.fieldsData = _.clone(this.flyer);
      this.blobImage = null;
    },
    onPhoneBlur() {
      if (!this.fieldsData.phone) this.fieldsData.extPhone = null;
    },
    onExtPhoneBlur(event) {
      const numbers = event.target.value.match(/\d/g);
      this.fieldsData.extPhone = numbers ? numbers.join('') : '';
    },
    delImage() {
      this.fieldsData.logo = this.NO_LOGO_IMAGE_URL;
      this.fieldsData.logoFileName = this.NO_LOGO_IMAGE;
      this.blobImage = null;
    },
    replace(event) {
      const fReader = new FileReader();
      if (event.target.files[0].type && (event.target.files[0].type !== 'image/jpeg') && (event.target.files[0].type !== 'image/png') && (event.target.files[0].type !== 'image/gif')) {
        this.$buefy.toast.open({ message: 'Please upload a file in the PNG, GIF, or JPG format', type: 'is-danger', duration: 3000 });
      } else {
        fReader.readAsDataURL(event.target.files[0]);
        const callShowEditImage = function handleImageLoaded() {
          this.showEditImage(fReader.result, true);
        };
        fReader.onloadend = callShowEditImage.bind(this);
      }
    },
    async getFlyerImages() {
      if (this.selectedPage?.id) {
        this.flyerFrontSide = null;
        this.flyerBackSide = null;
        const { data } = await getFlyerContractorImages(this.contractorId, this.selectedPage.id);
        if (data.previewImageFrontSide) {
          this.flyerFrontSide = data.previewImageFrontSide;
        }
        if (data.previewImageBackSide) {
          this.flyerBackSide = data.previewImageBackSide;
        }
        if (data.error) {
          this.$buefy.toast.open({ message: data.message, type: 'is-danger' });
        }
      }
    },
    showEditImage(file, modalIsActive) {
      const Component = Vue.extend(EditImage);
      this.cropModal = new Component({
        propsData: {
          show: modalIsActive,
          file,
        },
      });
      this.cropModal.$store = this.$store;
      this.cropModal.$mount();
      this.cropModal.$on('submit', (response) => {
        if (response) {
          this.blobImage = response;
          this.fieldsData.logo = URL.createObjectURL(response);
          this.$refs.flyer.removeChild(this.cropModal.$el);
        }
      });
      this.cropModal.$on('close', () => {
        this.$refs.flyer.removeChild(this.cropModal.$el);
      });
      this.$refs.flyer.appendChild(this.cropModal.$el);
      this.$refs.fileInput.value = '';
    },

    async submit() {
      this.submitDisabled = true;
      const formData = new FormData();
      if (this.blobImage) {
        const file = new File([this.blobImage], 'uploadImage', { type: 'image/png' });
        formData.set('logoFileBlob', file);
      }
      formData.set('id', this.contractorId);
      formData.set('pId', this.selectedPage.id);
      formData.set('pageName', this.selectedPage.pageName);

      if (this.fieldsData.logoFileName === this.NO_LOGO_IMAGE_URL
      || this.fieldsData.logoFileName === this.NO_LOGO_IMAGE) {
        this.fieldsData.logoFileName = null;
      }
      const flyer = {
        logoFileName: this.fieldsData.logoFileName,
        companyName: this.fieldsData.companyName,
        phone: this.fieldsData.phone,
        extPhone: this.fieldsData.extPhone,
        websiteUrl: this.fieldsData.websiteUrl,
        qrCodeBackside: this.fieldsData.qrCodeBackside,
      };

      if (this.fieldsData.logo === this.selectedPage.logo) {
        flyer.logoFileName = null;
      }
      if (this.blobImage || this.fieldsData.logo === this.flyer.logoFileName) {
        delete flyer.logoFileName;
      }
      formData.set('flyer', JSON.stringify(flyer));
      const { data } = await updateContractorPageMarketingMaterials(formData).catch(() => {
        this.submitDisabled = false;
        const btn = document.getElementById('flyer-submit-btn');
        btn.style.backgroundColor = '#68ba62';
      });
      if (data.error) {
        this.$buefy.toast.open({ message: data.message, type: 'is-danger' });
      } else {
        await this.updPages(this.contractorId);
        this.resetForm();
      }
      this.submitDisabled = false;
    },
    initFlyerParams() {
      if (this.selectedPage?.id) {
        this.flyer = _.clone(this.selectedPage.flyer);
        if (this.flyer.websiteUrl === null) {
          this.flyer.websiteUrl = this.contractorWebsiteUrl;
        }
        this.flyer.logo = this.flyer.logoFileName;
        if (this.flyer.qrCodeBackside === null) {
          this.flyer.qrCodeBackside = 'Co-branded page';
        }
      } else {
        this.flyer = { qrCodeBackside: 'Co-branded page' };
      }
    },
  },
};
</script>

<style lang="stylus" scoped>
.download-flyer
  height 48px
  font-size 19px
  @media(max-width:499px)
    width 100%
.button.edit-flyer.is-outlined
  color #68BA62
  border 1px solid #68BA62
  @media(max-width:499px)
    width 100%
  &:hover
    background-color #17B548
    border 1px solid #17B548
    color #ffffff
.extPhoneField
  max-width 100px
  >>>label.label
    white-space nowrap
.flyer-about-manage >>> div.control input.input
  height 48px
  box-shadow none!important
  border-radius 3px
.fields-contact-number-extension
  display flex
  @media screen and (max-width: 355px)
    flex-wrap wrap
  .contact-number
    min-width 150px
    margin-right 15px
.flyer
  &-form
    .qr-code-backside-field
      margin-bottom 30px
  .disable-has-addons.has-addons
    display: block
  p
    color: #525F66
  &-examples
    display: flex
    flex-direction: column
    align-items center
    @media(min-width:500px)
      flex-direction: row
      justify-content: space-between
    img
      box-shadow: 0 0 20px rgba(156 173 173 .3)
      margin-top: 20px
      width: 100%
      object-fit contain
      @media(min-width:500px)
        width: calc(50% - 10px)
  &-about
    justify-content space-between
    &.columns
      flex-direction: column
      @media(min-width:1336px)
        flex-direction: row
      > .column
        width: 100%
        @media(min-width:1336px)
          width: 50%
    &-use
      &-btn-box
        display flex
        justify-content flex-end
        margin-top: 30px
    &-manage-btn-box
      display flex
      justify-content flex-end
      flex-wrap wrap
      button
        height 48px
        min-width 123px
        font-size 19px
        font-weight 400
        text-transform uppercase
        @media screen and (max-width 480px)
          width 100%
      button:nth-child(2)
        margin-left: 20px
        @media screen and (max-width 480px)
          margin-top 15px
          margin-left 0
    &-manage-title
      margin-top: 20px
      margin-bottom: 20px
      font-size 18px;
      font-weight 700;
  &-about-item
    .flyer-form
      >>>label.label
        font-size 16px
      .qr-code-selector
        >>>span.select:after
          border-color #525F66
    border: 1px solid #8FA7B3
    height: 100%
    padding: 1rem 1rem
    @media(min-width:1540px)
      padding: 2rem 3rem
  .logo-buttons
    & button
      width: 100%
      &:not(last-child)
        margin-bottom: 10px

.personal-info__field.block-title
  margin-bottom 0.5rem
  >>>label.label
    font-size 16px

.field.image-section
  flex-wrap wrap
.field div.control >>> span.select
  height 48px
  select
    height 48px
    box-shadow none!important
    border-radius 3px
.image-section-btns
  display flex
  flex-direction column
  flex-wrap wrap
  @media screen and (max-width 480px)
    width 100%
  @media screen and (min-width 481px)
    max-width 150px
  .button
    height 48px
    font-size 19px
    &:nth-child(2n)
      margin-top 15px
  @media screen and (max-width: 355px)
    flex-direction column
.image-section-image-wrapper
  width 218px
  height 145px
  margin-right 15px
  margin-bottom 15px
  @media screen and (max-width: 475px)
    margin-bottom 15px
  img
    width 100%
    height 100%
    object-position left
    object-fit: contain
.p-logo
  padding-bottom 15px
</style>
